import { motion, useAnimation, useInView } from "framer-motion";
import { useRef, useEffect } from "react";
import { FaBuilding, FaProjectDiagram, FaUsers, FaClock } from "react-icons/fa";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.3 } },
};

const textVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { ease: "easeOut", duration: 0.8 } },
};

const statVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.25, duration: 0.6, ease: "easeOut" },
  }),
};

// Floating circle animation variants
const floatVariants = {
  animate: {
    y: [0, -20, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    },
  },
};

export default function WhyPartnerMJR() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [inView, controls]);

  const stats = [
    {
      icon: <FaClock className="text-[#b8860b] text-4xl mb-2" aria-hidden="true" />,
      value: 11,
      label: "Years in Operation",
    },
    {
      icon: <FaProjectDiagram className="text-[#b8860b] text-4xl mb-2" aria-hidden="true" />,
      value: 75,
      label: "Projects Completed",
    },
    {
      icon: <FaUsers className="text-[#b8860b] text-4xl mb-2" aria-hidden="true" />,
      value: 50,
      label: "Satisfied Clients",
    },
    {
      icon: <FaBuilding className="text-[#b8860b] text-4xl mb-2" aria-hidden="true" />,
      value: 5,
      label: "Provinces Served",
    },
  ];

  return (
    <section
      ref={ref}
      className="relative min-h-screen bg-gradient-to-tr from-[#0f2027] via-[#203a43] to-[#2c5364] text-white px-6 md:px-20 lg:px-40 py-32 overflow-hidden flex flex-col lg:flex-row items-center"
      aria-labelledby="why-partner-heading"
    >
      {/* Floating circles */}
      <motion.div
        className="absolute rounded-full bg-[#b8860b] opacity-10 w-40 h-40 top-20 left-10 pointer-events-none"
        variants={floatVariants}
        animate="animate"
      />
      <motion.div
        className="absolute rounded-full bg-[#b8860b] opacity-10 w-32 h-32 top-64 right-20 pointer-events-none"
        style={{ animationDelay: "2s" }}
        variants={floatVariants}
        animate="animate"
      />
      <motion.div
        className="absolute rounded-full bg-[#b8860b] opacity-10 w-48 h-48 bottom-32 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{ animationDelay: "4s" }}
        variants={floatVariants}
        animate="animate"
      />

      {/* Left Side Text Content */}
      <motion.div
        className="max-w-3xl z-10 lg:pr-20"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.h2
          variants={textVariants}
          id="why-partner-heading"
          className="text-3xl md:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#b8860b] via-[#c9a333] to-[#b8860b] text-transparent bg-clip-text mb-10 leading-tight drop-shadow-lg"
        >
          Why Partner <br /> with MJR Holdings
        </motion.h2>

        <motion.p
          variants={textVariants}
          className="text-base md:text-lg text-gray-300 leading-relaxed font-sans max-w-xl tracking-wide"
        >
          Since 2013, Matshinye Jr Holdings (Pty) Ltd has led the built environment sector
          with unmatched multidisciplinary expertise across civil, structural, mechanical, and
          electrical engineering. Our mission centers on delivering integrated, sustainable
          infrastructure solutions — blending cutting-edge technology, rigorous governance,
          and empowering local communities through skills transfer and ethical practices.
          <br />
          <br />
          Trust in MJR means partnering for excellence, resilience, and lasting positive impact.
        </motion.p>

        <motion.a
          variants={textVariants}
          whileHover={{ scale: 1.05, boxShadow: "0 0 15px rgba(184,134,11,0.9)" }}
          href="/servicesdetail"
          className="inline-block mt-14 bg-[#b8860b] text-[#0f2027] font-semibold text-base rounded-full px-14 py-4 shadow-lg hover:brightness-110 transition-all select-none"
          aria-label="Explore our services"
        >
          Explore Our Services
        </motion.a>
      </motion.div>

      {/* Right Side Stats */}
      <motion.div
        className="mt-16 lg:mt-0 flex-1 grid grid-cols-2 gap-12 max-w-md relative z-10"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        {stats.map(({ icon, value, label }, i) => (
          <motion.div
            key={label}
            className="flex flex-col items-center text-center"
            custom={i}
            variants={statVariants}
          >
            {icon}
            <span className="text-5xl font-extrabold text-[#b8860b]">{value}+</span>
            <span className="mt-2 text-lg text-gray-300 font-semibold">{label}</span>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
